import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useDownloadApi, useIndexApi, usePostApi, usePutApi, useShowApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { Institution, InstitutionForm, InstitutionSearchForm } from 'entities/institution'
import { useEffect } from 'react'

type InstitutionsResponse = PagingResponse & {
    results: Institution[]
}

export function useFetchInstitutionsApi(searchForm?: Form<InstitutionSearchForm>): IndexApiSet<InstitutionsResponse> & { execute: () => void } {
    const apiPath = 'institutions/'
    const api = useIndexApi<InstitutionsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useDownloadCsvInstitutionsApi(searchForm: Form<InstitutionSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const apiPath = 'institutions/csv/'
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        api.execute(apiPath, 'institution', searchForm.object)
    }

    return { ...api, execute: execute }
}

export function usePostInstitutionApi(): ApiSet<BaseResponse> & { execute: (form: Form<InstitutionForm>) => void } {
    const apiSet = usePostApi<BaseResponse, InstitutionForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { 
            formatJson: true,
            transform: (formData: InstitutionForm) => {
                const transformed = { ...formData };
                
                // webAddressが存在する場合、新しいAddress形式に変換
                if (formData.webAddress) {
                    transformed.webAddress = {
                        zipCode: formData.webAddress.zipCode,
                        prefecture: formData.webAddress.prefecture,
                        city: formData.webAddress.city,
                        line: formData.webAddress.line,
                        building: formData.webAddress.building,
                    };
                }

                // invoiceAddressが存在する場合、新しいAddress形式に変換
                if (formData.invoiceAddress) {
                    transformed.invoiceAddress = {
                        zipCode: formData.invoiceAddress.zipCode,
                        prefecture: formData.invoiceAddress.prefecture,
                        city: formData.invoiceAddress.city,
                        line: formData.invoiceAddress.line,
                        building: formData.invoiceAddress.building,
                    };
                }

                return transformed;
            }
        } as any // 一時的に型エラーを回避
    )

    const execute = (form: Form<InstitutionForm>) => {
        const apiPath = `institutions/`
        apiSet.execute(apiPath, form)
    }
    return { ...apiSet, execute: execute }
}

export function usePutInstitutionApi(): ApiSet<BaseResponse> & { execute: (form: Form<InstitutionForm>) => void } {
    const apiSet = usePutApi<BaseResponse, InstitutionForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<InstitutionForm>) => {
        const apiPath = `institutions/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutInstitutionsApi(): ApiSet<BaseResponse> & { execute: (form: Form<InstitutionForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, InstitutionForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<InstitutionForm[]>) => {
        const apiPath = `institutions/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteInstitutionApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `institutions/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}

export type IssueRequestForm = {
    amount?: number
}

export function useIssueRequestFormApi(): ApiSet<BaseResponse> & { execute: (id: number, form: Form<IssueRequestForm>) => void } {
    const apiSet = usePostApi<BaseResponse, IssueRequestForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (id: number, form: Form<IssueRequestForm>) => {
        const apiPath = `institutions/${id}/issue_request_form/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function useFetchPrintRequestInstitutionsApi(): IndexApiSet<InstitutionsResponse> & { execute: () => void } {
    const apiPath = 'print_requests/'
    const api = useIndexApi<InstitutionsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
    })

    const execute = (): void => {
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}

/**
 * 利用可能な依頼書残数を0にリセットする
 */
export type RequestFormResettableProductType = 'scan' | 'check' | 'lite'
export function usePostUpdateAllRequestFormNotCountedInStockApi(): ApiSet<BaseResponse> & { execute: (id: number, productType: RequestFormResettableProductType) => void } {
    const apiSet = usePostApi<BaseResponse, any>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (id: number, productType: RequestFormResettableProductType) => {
        const apiPath = `institutions/${id}/update_all_request_form_not_counted_in_stock/?product_type=${productType}`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

type AvailableRequestFormCountsResponse = BaseResponse & {
    scan: number
    check: number
    lite: number
}
export function useFetchAvailableRequestFormCountsApi(institutionId?: number): ApiSet<AvailableRequestFormCountsResponse> & { execute: (institutionId: number) => void } {
    const api = useShowApi<AvailableRequestFormCountsResponse>(new CraifHttpClient(), {
        initialResponse: { scan: 0, check: 0, lite: 0 },
    })

    const execute = (id: number): void => {
        console.log('execute fetch available request form counts', id)
        api.execute(`institutions/${id}/available_counts/`)
    }

    useEffect(() => {
        if (institutionId) execute(institutionId)
    }, [institutionId])

    useEffect(() => {
        if (api.response) {
            console.log('利用可能な依頼書数が更新されました:', api.response)
        }
    }, [api.response])

    return { ...api, execute }
}